import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { NavMenu } from './NavMenu';
import { groupNav, NAV_EXTRA_ROUTES, underRoute, type NavGroupDef } from './nav-groups';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import { useCart } from '../api/hooks';
import { LanguageSwitcher } from '../i18n/LanguageSwitcher';
import { Chip } from '../ui';
import { ProfileMenu } from './ProfileMenu';
import { NotificationBell } from './NotificationBell';

/**
 * The retailer portal's chrome.
 *
 * Deliberately quieter than the console: fewer sections, comfortable density
 * (UI-08), and no portal switcher — a retailer has one account on one portal
 * and that is not a choice they make.
 *
 * RL-05 — the sections come from `me.pages`, so the three people a trade
 * customer signs in as see three different portals: a Buyer orders and never
 * sees the ledger, an Accounts user settles and never sees prices, an Owner does
 * both. That separation is the reason a retailer wants more than one login.
 */
/** R-05 cart review — reached from the header icon, not the sub-menu. */
const CART_PAGE = 'R-05';
/** The "Shop" tab is not a page of its own: it stands for the catalogue screens (sub-menu below). */
const SHOP_TAB = 'shop';

/** Top-bar order for a retailer: find and buy, then follow it, then what is held, then money. */
const RETAILER_GROUPS = (t: (k: string) => string): readonly NavGroupDef[] => [
  { label: '', ids: ['R-01'] },
  { label: '', ids: [SHOP_TAB] },
  { label: '', ids: ['R-06'] },
  { label: '', ids: ['R-23'] },
  { label: t('page.groupInventory'), ids: ['R-20', 'R-21', 'R-25'] },
  { label: t('page.groupFinance'), ids: ['R-16', 'R-22'] },
  { label: '', ids: ['R-18'] },
];

export function ShopShell() {
  const { me, signOut } = useSession();
  const { t } = useTranslation();
  const location = useLocation();
  const canViewCart = me?.kind === 'retailer'
    && (me.permissions.includes('shop.order') || me.permissions.includes('shop.order_view'));
  // Read here, not just in the screens that write it — the badge has to be
  // right on every page, not only on the one that happens to fetch it anyway.
  const { data: cart } = useCart({ enabled: !!canViewCart });
  if (!me || me.kind !== 'retailer') return null;

  /* The menu is server-authored (`access/pages.ts`), so its labels arrive in
     English. Translated by page ID rather than by text, with the server's name
     as the fallback: a screen that ships before its key does still gets a menu
     entry, in English, instead of rendering `page.R-21` at a customer.
     Every granted page in the catalog module (R-02 Catalog, R-04 bulk entry,
     R-05 cart review — R-03's product detail carries no sortOrder, so it was
     already excluded from any menu) becomes a child of one "Shop" parent, the
     two-tier structure the reference draws. Nothing outside that module
     changes shape — Statement and Account stay flat top-level items, and
     nothing is invented for Dashboard/Orders/Finance, which have no pages to
     group in the first place. */
  const granted = me.pages.filter((p) => p.granted && p.sortOrder !== null);
  /* The cart is reached from the icon in the header, which carries the item
     count, so R-05 is not repeated as a second "Cart" entry in the sub-menu. */
  const shopPages = granted
    .filter((p) => p.module === 'catalog' && p.id !== CART_PAGE)
    .map((p) => ({ to: p.route, label: t(`page.${p.id}`, { defaultValue: p.name }) }));
  const NAV = granted
    .filter((p) => p.module !== 'catalog')
    .map((p) => ({ id: p.id, to: p.route, label: t(`page.${p.id}`, { defaultValue: p.name }) }));
  const shopActive = shopPages.some((p) => underRoute(location.pathname, p.to));
  /* `NavLink`'s own partial match would light up more than one tab here:
     R-04 `/shop/order` and R-05 `/shop/order/review` are siblings, not
     parent and child, but R-05's path is a sub-path of R-04's, so on
     `/shop/order/review` both would otherwise claim a match. The longest
     (most specific) matching route wins instead of every route that matches
     at all — Catalog's own `/shop/catalog/:code` product page still lights
     up "Catalog" the same way, since nothing else in `shopPages` is more
     specific than it there. */
  const activeShopTab = shopPages
    .filter((p) => underRoute(location.pathname, p.to))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;

  return (
    <>
      <div className="mk-stripe" />
      <div className="mk-top">
        <div className="mk-top-in">
          {/* The wordmark is a brand name, not copy. It reads the same in every
              language and is the one thing on these screens that must not be
              translated. */}
          {/* eslint-disable no-restricted-syntax */}
          <NavLink className="mk-wm" to="/shop/catalog">
            MIRAKI<i>MIRACULOUS · RARE · KINDRED</i>
          </NavLink>
          {/* eslint-enable no-restricted-syntax */}
          <NavMenu
            entries={groupNav(
              [
                ...(shopPages.length ? [{ id: SHOP_TAB, to: shopPages[0]!.to, label: t('page.shop') }] : []),
                ...NAV,
              ],
              RETAILER_GROUPS(t),
            )}
            isActive={(id) => {
              if (id === SHOP_TAB) return shopActive;
              // An order's detail and its consignment invoices belong to Orders, not to a section of their own.
              const extra = NAV_EXTRA_ROUTES[id];
              return extra ? extra.some((r) => underRoute(location.pathname, r)) || undefined : undefined;
            }}
          />
          <div className="mk-tools">
            {canViewCart ? (
              <NavLink className="mk-ibtn" to="/shop/order/review" aria-label={t('shop.chrome.cart')} title={t('shop.chrome.cart')}>
                {/* A shopping cart: basket on a frame with a handle, two wheels. */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                  <path d="M2.5 3.5h2.6l2.1 10.2a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.76l1.6-6.74H6.1" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9.5" cy="19" r="1.4" />
                  <circle cx="17" cy="19" r="1.4" />
                </svg>
                <span className="mk-badge">
                  {cart?.lines.reduce((sum, l) => sum + l.quantity, 0) ?? 0}
                </span>
              </NavLink>
            ) : null}
            {/* On hold is not a detail: it changes what the account may do, so it
                is on every screen rather than buried in Account. */}
            {me.retailer.status !== 'ACTIVE' ? (
              <Chip tone="stop">{me.retailer.status.replace('_', ' ')}</Chip>
            ) : null}
            <NotificationBell base="shop" />
            <span className="mk-tools-sep" aria-hidden />
            {/* The account is the identity here, not the person: a Buyer and an
                Accounts user at one trade customer need to see which account
                they are acting for before they see their own name. */}
            <ProfileMenu
              name={me.retailer.name}
              subtitle={me.name}
              email={me.email}
              signOutLabel={t('common.signOut')}
              onSignOut={() => void signOut()}
            >
              <LanguageSwitcher />
            </ProfileMenu>
          </div>
        </div>
      </div>
      {shopActive ? (
        <div className="mk-subnav">
          <div className="mk-subnav-in">
            {shopPages.map((p) => (
              <NavLink key={p.to} to={p.to} className={p.to === activeShopTab ? 'on' : ''}>
                {p.label}
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}
      <main><Outlet /></main>
    </>
  );
}
